import { useEffect, useMemo, useState } from 'react';
import { Button, Group, Loader, Modal, Text, UnstyledButton } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Matcher, MatcherSlot } from '@nelnela/contracts';
import { api, ApiError } from '../../lib/api';

/**
 * "Save as pattern" (04-flows F9b, decision D6). The learner selected a phrase; the model proposes
 * the FRAME behind it, and the sheet shows that frame as plain-language chips they can adjust —
 * one tap turns a fixed word into "any word" and back. The raw matcher lives under "advanced",
 * where a curious learner can see exactly what will be matched and nobody else has to.
 *
 * The feedback loop is the point: "matches N lines you've watched" is recomputed from the CURRENT
 * chips (`patternPreview`), so an adjustment that makes the frame useless says so before it is
 * saved.
 */

export interface PatternSheetProps {
  opened: boolean;
  onClose: () => void;
  selection: string;
  sentence: string;
  lineId?: string;
  onSaved: (message: string) => void;
}

/** The chip label for one slot — the same vocabulary `templateOf` uses on the server. */
function chipLabel(slot: MatcherSlot): string {
  switch (slot.kind) {
    case 'literal':
      return slot.text;
    case 'lemma':
      return slot.label ?? `${slot.lemma} (any form)`;
    case 'suffix':
      return slot.label ?? `-${slot.text}`;
    case 'prefix':
      return slot.label ?? `${slot.text}-`;
    case 'pos':
      return slot.label ?? `any ${slot.pos}`;
    case 'slot':
      return slot.label ? `any ${slot.label}` : 'any word';
    case 'gap':
      return `up to ${slot.max} words`;
    case 'feats':
      return slot.label ?? 'any word';
  }
}

/**
 * One tap toggles a fixed word into a free slot and back — the whole "Adjust" affordance.
 *
 * A free slot can only become a literal again if we know which WORD it was: a slot's label is the
 * model's English chip text ("verb"), and storing that as a literal would produce a matcher that
 * can never match Georgian. `flip` supplies the original slot for that direction; without one, the
 * slot is not adjustable.
 */
function toggleSlot(slot: MatcherSlot, original?: MatcherSlot): MatcherSlot {
  if (slot.kind === 'literal' || slot.kind === 'lemma') return { kind: 'slot', label: 'word' };
  if (
    slot.kind === 'slot' &&
    original &&
    (original.kind === 'literal' || original.kind === 'lemma')
  ) {
    return original;
  }
  return slot;
}
const isAdjustable = (slot: MatcherSlot, original?: MatcherSlot): boolean =>
  slot.kind === 'literal' ||
  slot.kind === 'lemma' ||
  (slot.kind === 'slot' && (original?.kind === 'literal' || original?.kind === 'lemma'));

export function PatternSheet({
  opened,
  onClose,
  selection,
  sentence,
  lineId,
  onSaved,
}: PatternSheetProps) {
  const [matcher, setMatcher] = useState<Matcher | null>(null);
  const [displayForm, setDisplayForm] = useState('');
  const [advanced, setAdvanced] = useState(false);
  /** the words the learner turned back into literals keep their original text */
  const [original, setOriginal] = useState<MatcherSlot[]>([]);

  const draft = useQuery({
    queryKey: ['patternDraft', selection, sentence],
    queryFn: () =>
      api.patternDraft({
        targetLang: 'ka',
        baseLang: 'en',
        selection,
        sentence,
        ...(lineId ? { lineId } : {}),
      }),
    enabled: opened && selection.length > 0,
    retry: false,
    staleTime: 10 * 60_000,
  });

  // `opened` is a dependency on purpose: closing nulls `matcher`, and re-opening for the same
  // selection inside `staleTime` hands back the IDENTICAL cached object — an effect keyed only on
  // `draft.data` would not re-run and the modal would render an empty body.
  useEffect(() => {
    if (!opened || !draft.data) return;
    setMatcher(draft.data.matcher);
    setDisplayForm(draft.data.displayForm);
    setOriginal(draft.data.matcher.slots);
  }, [draft.data, opened]);
  useEffect(() => {
    if (!opened) {
      setMatcher(null);
      setAdvanced(false);
    }
  }, [opened]);

  const preview = useQuery({
    queryKey: ['patternPreview', matcher],
    queryFn: () => api.patternPreview({ targetLang: 'ka', baseLang: 'en', matcher: matcher! }),
    enabled: opened && matcher !== null,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () =>
      api.itemCreate({
        targetLang: 'ka',
        baseLang: 'en',
        kind: 'pattern',
        lemma: template,
        displayForm,
        matcher: matcher!,
        context: { surface: selection, ...(lineId ? { lineId } : {}) },
      }),
    onSuccess: () => {
      onSaved('Pattern saved — it will be marked from now on.');
      onClose();
    },
  });

  const template = useMemo(
    () =>
      matcher ? matcher.slots.map((s) => (s.kind === 'slot' ? '___' : chipLabel(s))).join(' ') : '',
    [matcher],
  );

  const flip = (i: number) => {
    if (!matcher) return;
    const slots = matcher.slots.map((s, j) => (j === i ? toggleSlot(s, original[j]) : s));
    setMatcher({ ...matcher, slots });
  };

  const failed = draft.error instanceof ApiError ? draft.error.problem.title : null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Save as pattern"
      centered
      size="md"
      styles={{ content: { background: 'var(--nn-panel)' } }}
    >
      {draft.isPending && (
        <Group gap={8}>
          <Loader size="sm" />
          <Text fz={13} c="dimmed">
            Finding the frame behind “{selection}”…
          </Text>
        </Group>
      )}
      {failed && (
        <Text fz={13} c="dimmed">
          {failed}
        </Text>
      )}
      {matcher && (
        <>
          <Text fz={15} fw={500} lang="ka">
            {displayForm}
          </Text>
          {draft.data?.explanation && (
            <Text fz={12.5} c="dimmed" mt={4}>
              {draft.data.explanation}
            </Text>
          )}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
            {matcher.slots.map((slot, i) => (
              <UnstyledButton
                key={i}
                onClick={() => flip(i)}
                disabled={!isAdjustable(slot, original[i])}
                aria-label={`${chipLabel(slot)} — tap to change`}
                style={{
                  padding: '5px 10px',
                  borderRadius: 999,
                  fontSize: 12.5,
                  border: '1px solid var(--nn-hairline)',
                  background: slot.kind === 'slot' ? 'transparent' : 'var(--nn-panel-2)',
                  color: slot.kind === 'slot' ? 'var(--nn-faint)' : 'var(--nn-text)',
                  fontStyle: slot.kind === 'slot' ? 'italic' : undefined,
                }}
              >
                {chipLabel(slot)}
              </UnstyledButton>
            ))}
          </div>
          <Text fz={11} c="dimmed" mt={6}>
            Tap a chip to switch between the exact word and “any word”.
          </Text>

          <div
            style={{
              marginTop: 12,
              padding: '9px 11px',
              borderRadius: 8,
              background: 'var(--nn-hairline)',
            }}
          >
            {preview.isPending ? (
              <Text fz={12.5} c="dimmed">
                Checking what this matches…
              </Text>
            ) : preview.data ? (
              <>
                <Text fz={13}>
                  Matches <b>{preview.data.matchCount}</b>{' '}
                  {preview.data.matchCount === 1 ? 'line' : 'lines'} you’ve watched.
                </Text>
                {preview.data.examples.slice(0, 2).map((ex) => (
                  <Text key={ex.lineId} fz={12} c="dimmed" mt={3} lang="ka">
                    {ex.text}
                  </Text>
                ))}
                {preview.data.matchCount === 0 && (
                  <Text fz={11.5} c="dimmed" mt={3}>
                    Nothing yet — that is fine for a frame you have just met.
                  </Text>
                )}
              </>
            ) : (
              <Text fz={12.5} c="dimmed">
                Couldn’t check this one.
              </Text>
            )}
          </div>

          <UnstyledButton
            onClick={() => setAdvanced((a) => !a)}
            aria-expanded={advanced}
            style={{
              marginTop: 10,
              fontSize: 10,
              letterSpacing: '.07em',
              color: 'var(--nn-faint)',
              textTransform: 'uppercase',
            }}
          >
            {advanced ? '▾' : '▸'} Advanced
          </UnstyledButton>
          {advanced && (
            <pre
              style={{
                marginTop: 6,
                fontSize: 11,
                lineHeight: 1.5,
                color: 'var(--nn-muted)',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {JSON.stringify(matcher, null, 2)}
            </pre>
          )}

          <Group gap={8} mt={14} justify="flex-end">
            <Button size="xs" variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="xs"
              variant="default"
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save pattern
            </Button>
          </Group>
          {save.error && (
            <Text fz={12} c="dimmed" mt={6}>
              {save.error instanceof ApiError
                ? save.error.problem.title
                : 'Couldn’t save that one.'}
            </Text>
          )}
        </>
      )}
    </Modal>
  );
}
